import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  output,
  signal,
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatTooltip } from "@angular/material/tooltip";
import { MatMenuTrigger } from "@angular/material/menu";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TranslateUtils } from "../../utils/translate.utils";
import { formatNumeric } from "../../utils/format-numeric";
import { IconService } from "../../services/icon.service";
import { ChipState } from "../../enums/chip-state.enum";
import { NgTemplateOutlet } from "@angular/common";

/**
 * Nom de repli d'une puce cliquable ICÔNE SEULE sans `ariaLabel` ni infobulle : seulement
 * les icônes dont le sens ne fait pas de doute (pas `close` / `clear` : fermer ? effacer ?).
 */
const ICON_NAMES: Record<string, string> = {
  chevron_left: "button.precedent",
  navigate_before: "button.precedent",
  keyboard_arrow_left: "button.precedent",
  arrow_back: "button.precedent",
  chevron_right: "button.next",
  navigate_next: "button.next",
  keyboard_arrow_right: "button.next",
  arrow_forward: "button.next",
  add: "button.add",
  add_circle_outline: "button.add",
  delete: "button.delete",
  delete_outline: "button.delete",
  edit: "button.edit",
  refresh: "button.refresh",
  save: "button.save",
  content_copy: "button.copy",
};

@Component({
  selector: "vl-chips",
  standalone: true,
  imports: [MatIcon, MatProgressSpinner, NgTemplateOutlet],
  templateUrl: "./chips.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: "./chips.component.scss",
  // `aria-pressed` est RECOPIÉ sur l'hôte (le rôle bouton reste sur le div interne) : c'est
  // l'élément qui porte le `data-testid`, celui que les POM relisent avant de basculer
  // (`poserFiltreBandeauDue`, suivre / ne plus suivre). Sans rôle, l'hôte reste générique
  // pour les lecteurs d'écran (axe ne le signale pas — vérifié le 28/09/2026).
  host: {
    "[attr.aria-pressed]": "ariaPressed()",
  },
})
export class ChipsComponent implements OnInit, AfterViewInit, OnDestroy {
  iconRight = input<string>();
  iconLeft = input<string>();
  iconColor = input<string>("inherit");
  roundedValue = input<string | number>();
  disabled = input(false);
  contentHeight = input(true);
  loading = input(false);
  state = input<ChipState>("enabled");
  size = input<"big" | "small" | "tiny">("big");
  bubble = input<number | string>();
  bubbleBgClass = input("bg-secondary");
  bubbleColorClass = input("on-secondary");
  showBubbleZero = input<boolean>(false);
  stopPropagation = input<boolean>(true);
  contentOnHover = input<boolean>(false);
  isOpen = signal(false);
  showText = signal(false);
  /**
   * Faux tant que le PREMIER rendu n'est pas peint : la classe
   * `.no-transition` neutralise alors toutes les transitions CSS. Un élément
   * qui APPARAÎT ne doit jamais « pousser » depuis un état fantôme (retour UX
   * 09/07 : chips remontées par un @switch qui semblaient grandir) — les
   * transitions (hover, contentOnHover, géométrie) restent intactes ensuite.
   * Double rAF : garantit qu'un paint a eu lieu avec les styles initiaux.
   */
  transitionsReady = signal(false);

  isAnimating = signal(false);

  hovering = signal(false);
  private hoverLeaveTimer: ReturnType<typeof setTimeout> | null = null;
  private opacityTimer: ReturnType<typeof setTimeout> | null = null;
  closeGrace = signal(false);

  unclampText = signal(false);

  _stopPropagation = computed(() => {
    if (this.cursor() === "default") {
      return false;
    }
    return this.stopPropagation();
  });
  /**
   * ⚠️ `@Output` + `EventEmitter` (et non `output()`) À DESSEIN : `observed` dit si un
   * `(action)` est branché — c'est ce qui fait d'une puce un BOUTON au clavier
   * (`isInteractive`). `output()` n'expose pas ses abonnés.
   */
  @Output() action = new EventEmitter<void>();
  iconRightAction = output();
  /**
   * Clavier (CL-17) : `true` force le rôle bouton (puce cliquable par un `(click)` posé
   * sur l'hôte), `false` le retire ; absent → bouton dès qu'un `(action)` est branché.
   */
  interactive = input<boolean | undefined>(undefined);
  /** Nom lu par les lecteurs d'écran (puce cliquable) — indispensable pour une puce icône seule. */
  ariaLabel = input<string>();
  /**
   * Puce-BASCULE (filtre « seulement… », jour coché) : `aria-pressed` sur le bouton (et
   * recopié sur l'hôte). Absent (défaut) → rien n'est posé. Sans effet sur une puce
   * d'affichage. L'apparence reste celle du `state` choisi par l'appelant.
   */
  pressed = input<boolean | undefined>(undefined);
  /** Puce qui ouvre/replie quelque chose (hors `mat-menu`, qui pose déjà le sien) : `aria-expanded`. */
  expanded = input<boolean | undefined>(undefined);
  /**
   * Position dans un groupe SOUDÉ (bouton scindé « Suivre | ▾ ») : `first` / `middle` / `last`
   * aplatissent les coins côté voisin — les classes de `vl-chips-toggle`, ouvertes à la puce seule.
   */
  segment = input<"first" | "middle" | "last" | null>(null);
  /**
   * Puce SUPPRIMABLE (une personne choisie, une heure) : un vrai `<button>` « × » après le
   * contenu — tabulable, nommé (`removeAriaLabel`, obligatoire : « Retirer Marie Durand »),
   * cible ≥ 44 px au doigt — qui émet `(remove)` sans déclencher `(action)`.
   * Différent de `iconRight` + `(iconRightAction)` (un glyphe cliquable, hors clavier), gardé
   * pour les usages existants.
   */
  removable = input(false);
  removeAriaLabel = input<string>();
  removeTestId = input<string>();
  remove = output<void>();
  private hasActionListener = signal(false);
  /** Bouton ET bascule : `"true"` / `"false"` ; sinon rien (puce d'affichage, `pressed` absent). */
  ariaPressed = computed(() => {
    const p = this.pressed();
    return this.isInteractive() && p !== undefined ? String(p) : null;
  });
  ariaExpanded = computed(() => {
    const e = this.expanded();
    return this.isInteractive() && e !== undefined ? String(e) : null;
  });
  /**
   * Une puce CLIQUABLE est un bouton : rôle, tabulation, Entrée / Espace, nom. Une puce
   * d'affichage (statut, compteur) reste inerte — elle n'encombre pas la tabulation.
   * `cursor="default"` dit « pas cliquable maintenant » (ex. `[cursor]="x ? 'pointer' : 'default'"`).
   */
  isInteractive = computed(() => (this.interactive() ?? this.hasActionListener()) && this.cursor() !== "default");
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  /** L'infobulle posée sur l'hôte (`<vl-chips matTooltip="…">`) : le nom naturel d'une puce icône seule. */
  private tooltip = inject(MatTooltip, { self: true, optional: true });
  /**
   * Le `matMenuTriggerFor` posé sur l'hôte (`vl-select`, « Suivre ▾ », « Par trousseau ») : à la
   * fermeture du menu, Material rend le focus à l'HÔTE — qui n'est pas focalisable (le bouton est
   * le div interne) — et le focus tombait sur `<body>` : au clavier, on repartait du haut de la page
   * après chaque choix (constaté le 28/09/2026). La puce le reprend, seulement s'il a été perdu.
   */
  private menuTrigger = inject(MatMenuTrigger, { self: true, optional: true });
  /**
   * La puce a-t-elle un texte à elle ? Mesuré une fois la vue rendue (le contenu projeté
   * passe par un `ngTemplateOutlet` : absent quand les liaisons du div sont évaluées), puis
   * au focus (un texte arrivé après coup). `null` = pas encore mesuré → on suppose un texte.
   */
  private ownText = signal<boolean | null>(null);

  /**
   * Nom accessible d'une puce cliquable : `ariaLabel` ; à défaut, pour une puce ICÔNE SEULE
   * (sans texte à elle), son infobulle, sinon le sens de l'icône quand il est sans ambiguïté
   * (`ICON_NAMES`). Une puce qui a un texte le garde pour nom (rien n'est posé).
   */
  accessibleName(): string | null {
    if (!this.isInteractive()) return null;
    if (this.ariaLabel()) return this.ariaLabel()!;
    if (this.ownText() ?? true) return null;
    const tip = this.tooltip?.message;
    if (typeof tip === "string" && tip.trim()) return tip;
    const key = ICON_NAMES[this.iconLeft() ?? ""] ?? ICON_NAMES[this.iconRight() ?? ""];
    return key ? TranslateUtils.translate(key) : null;
  }

  /** Relit le texte propre (vue rendue, focus) — seulement pour une puce cliquable. */
  measureOwnText(): void {
    if (this.isInteractive()) this.ownText.set(this.hasOwnText());
  }

  /** Texte propre de la puce : ses nœuds texte, hors glyphes (`mat-icon`), spinner, pastille et bulle. */
  private hasOwnText(): boolean {
    const root = this.host.nativeElement;
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (!n.textContent?.trim() || (n as Text).parentElement?.closest("mat-icon, mat-spinner, .pellet, .top-alert")) continue;
      return true;
    }
    return false;
  }
  left = computed(() => {
    const length = this.bubbleFormatted().length;
    if (length < 4) {
      return "calc(100% - 9px)";
    }
    return "calc( 100% - 14px)";
  });
  cursor = input<"pointer" | "grab" | "default">("pointer");
  /** `stacked` (2026-08-06) : texte SOUS l'icône, compact — pour les
   *  cellules étroites (ex. tableaux condensés). Miroir du type stacked
   *  de vl-big-button. */
  layout = input<"row" | "stacked">("row");

  bubbleFormatted = computed(() => {
    const value = this.bubble();
    return typeof value === "string"
      ? value
      : formatNumeric(value, 0);
  });
  _bubbleClasses = computed(() => {
    return `${this.bubbleBgClass()} ${this.bubbleColorClass()}`;
  });
  colorClass = computed(() => {
    return `${this.disabled() ? "chips-disabled cursor-not-allowed" : "cursor-" + this.cursor()} chips-${this.state()} chips-${this.size()}${this.layout() === "stacked" ? " chips-stacked" : ""}`;
  });

  constructor() {
    this.menuTrigger?.menuClosed.pipe(takeUntilDestroyed()).subscribe(() => this.restoreFocusAfterMenu());
    // Transitions neutralisées jusqu'au premier paint (cf. transitionsReady).
    afterNextRender(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.transitionsReady.set(true)),
      );
    });
    effect(
      () => {
        const h = this.hovering();

        this.isAnimating.set(true);

        if (h) {
          this.isOpen.set(true);
          this.showText.set(false);
          this.unclampText.set(false);
        } else {
          this.unclampText.set(false);
          this.isOpen.set(false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit() {
    // Les `(action)` du gabarit parent sont branchés à la création, avant le premier ngOnInit.
    this.hasActionListener.set(this.action.observed);
  }

  ngAfterViewInit() {
    this.measureOwnText();
  }

  /**
   * Entrée / Espace sur une puce cliquable focalisée = un clic sur la puce : même chemin
   * (désactivée / en chargement → rien, `stopPropagation`). La touche ne remonte pas :
   * un parent qui écoute aussi Entrée (carte) n'agirait pas en double.
   */
  onKeydown(event: KeyboardEvent) {
    if (!this.isInteractive()) return;
    if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
    if (event.target !== event.currentTarget || event.isComposing) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat) return;
    (event.currentTarget as HTMLElement).click();
  }

  ngOnDestroy() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
    }
    if (this.opacityTimer) {
      clearTimeout(this.opacityTimer);
      this.opacityTimer = null;
    }
  }

  onAction($event: MouseEvent) {
    if (this._stopPropagation()) {
      $event.stopPropagation();
    }
    if (!this.disabled() && !this.loading()) {
      this.action.emit();
    }
  }

  /**
   * Focus perdu à la fermeture du menu de l'hôte → rendu au bouton de la puce. « Perdu » = sur
   * `<body>`, ou encore DANS le panneau qui se ferme : sans contenu paresseux (`vl-select`),
   * `menuClosed` part pendant l'animation de sortie, l'option garde le focus… jusqu'au retrait du
   * panneau, qui le jette sur `<body>`. Ailleurs (un dialog ouvert par l'option) : on n'y touche pas.
   */
  private restoreFocusAfterMenu(): void {
    setTimeout(() => {
      const doc = this.host.nativeElement.ownerDocument;
      const active = doc.activeElement as HTMLElement | null;
      const lost = !active || active === doc.body || !!active.closest(".mat-mdc-menu-panel");
      if (!this.isInteractive() || !lost) return;
      this.host.nativeElement.querySelector<HTMLElement>(":scope > div.chips")?.focus({ preventScroll: true });
    });
  }

  /** « × » d'une puce supprimable : ne remonte ni à la puce (`action`) ni à un `(click)` de l'hôte. */
  onRemove($event: MouseEvent) {
    $event.stopPropagation();
    if (!this.disabled() && !this.loading()) {
      this.remove.emit();
    }
  }

  isSvgIcon(s: string) {
    return IconService.isSvgIcon(s);
  }

  onHoverEnter() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
    }
    if (this.opacityTimer) {
      clearTimeout(this.opacityTimer);
      this.opacityTimer = null;
    }
    this.closeGrace.set(false);
    this.hovering.set(true);
    this.isAnimating.set(true);
    this.unclampText.set(false);

    this.opacityTimer = setTimeout(() => {
      if (this.hovering() || this.closeGrace()) {
        this.showText.set(true);
      }
      this.opacityTimer = null;
    }, 100);
  }

  onHoverLeave() {
    if (this.hoverLeaveTimer) {
      clearTimeout(this.hoverLeaveTimer);
    }
    this.unclampText.set(false);

    this.closeGrace.set(true);

    this.hoverLeaveTimer = setTimeout(() => {
      this.hovering.set(false);
      this.isAnimating.set(false);
      this.hoverLeaveTimer = null;
    }, 500);
  }

  onWidthTransitionEnd(event: TransitionEvent) {
    if (event.propertyName !== "grid-template-columns") return;

    if (this.hovering()) {
      this.unclampText.set(true);
    } else {
      this.showText.set(false);
      this.closeGrace.set(false);
    }
  }

  onOpacityTransitionEnd(event: TransitionEvent) {
    if (event.propertyName !== "opacity") return;

    if (!this.hovering()) {
      this.isOpen.set(false);
    }

    this.isAnimating.set(false);
  }
}
